import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import client, { formatApiError } from "@/lib/api";

const CATS = ["Automotriz", "Industrial", "Naval", "Suministros"];
const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", category: "Industrial", author: "Equipo GEMA", read_time: "5 min", published: true };
const inputCls = "bg-gema-surface border-white/15 text-white rounded-none focus-visible:ring-gema-red";

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/admin/blog").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ title: it.title, excerpt: it.excerpt, content: it.content, cover_image: it.cover_image, category: it.category, author: it.author, read_time: it.read_time, published: it.published });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await client.put(`/admin/blog/${editing.id}`, form);
      else await client.post("/admin/blog", form);
      toast.success(editing ? "Artículo actualizado" : "Artículo creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Error", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este artículo?")) return;
    await client.delete(`/admin/blog/${id}`);
    toast.success("Artículo eliminado");
    load();
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold uppercase text-white text-lg">Artículos del Blog ({items.length})</h2>
        <button onClick={openNew} data-testid="blog-new-btn" className="btn-primary py-2.5 px-5"><Plus className="h-4 w-4" /> Nuevo</button>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-4 hover:bg-gema-surface transition-colors">
            <img src={it.cover_image} alt="" className="h-12 w-16 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{it.title}</p>
              <p className="text-white/40 text-xs mt-1">{it.category} · {it.published ? "Publicado" : "Borrador"}</p>
            </div>
            <button onClick={() => openEdit(it)} data-testid={`blog-edit-${it.id}`} className="p-2 text-white/60 hover:text-gema-red transition-colors"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(it.id)} data-testid={`blog-delete-${it.id}`} className="p-2 text-white/60 hover:text-gema-red transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-white/40 text-sm">Sin artículos aún.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gema-ink border border-white/10 text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing ? "Editar" : "Nuevo"} artículo</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="blog-form">
            <div><Label className="text-white/70 text-xs uppercase">Título</Label><Input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-xs uppercase">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gema-ink border-white/15 text-white">{CATS.map((c) => <SelectItem key={c} value={c} className="focus:bg-gema-red focus:text-white">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-white/70 text-xs uppercase">Tiempo de lectura</Label><Input className={inputCls} value={form.read_time} onChange={(e) => set("read_time", e.target.value)} /></div>
            </div>
            <div><Label className="text-white/70 text-xs uppercase">Imagen (URL)</Label><Input className={inputCls} value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} required /></div>
            <div><Label className="text-white/70 text-xs uppercase">Extracto</Label><Textarea rows={2} className={inputCls} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} required /></div>
            <div><Label className="text-white/70 text-xs uppercase">Contenido (usa doble salto de línea para separar párrafos)</Label><Textarea rows={7} className={inputCls} value={form.content} onChange={(e) => set("content", e.target.value)} required /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="blog-published" />
              <Label className="text-white/70 text-sm">Publicado</Label>
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
